import { ContentSwitcher, IconButton, LabelChip } from "@carbon6/c6-frontend-design";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { listTrainingEvalRunRecordedVideos, type TrainingEvalVideoView } from "@/api";
import { type LoadState, resolveLoadErrorMessage } from "@/components/dataset";
import { isHttpClientError } from "@/http";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import {
  MultiChannelVideoPlayer,
  type VideoChannelSource,
} from "@/components/video/MultiChannelVideoPlayer";
import {
  episodeTitle,
  isFailureEpisode,
  isSuccessEpisode,
  playableCameras,
  useRecordedEpisodeGallery,
  type RecordedEpisode,
  type RecordedEpisodeFilter,
} from "@/modules/evaluation/public";
import { hasMissingFailureVideos } from "./sessionAnalysisModel";
import styles from "./ScenarioEvalSection.module.css";

const filterLabels: Record<RecordedEpisodeFilter, string> = {
  all: "전체",
  failures: "실패",
  successes: "성공",
};

type SessionEvalVideoGalleryProps = {
  evalRunId: string;
  // 지표로 계산한 실패 에피소드 수. 실패 영상 부재 경고의 판정 입력이다. 산출 전이면 null 이다.
  expectedFailures: number | null;
};

// 평가 영상 갤러리(마운트할 때 지연 로드). 실패 우선 필터가 기본이다. 실패가 있었는데 실패 영상이
// 없으면 경고로 크게 표시한다(실패 영상은 필수 산출물이다).
export function SessionEvalVideoGallery({
  evalRunId,
  expectedFailures,
}: SessionEvalVideoGalleryProps): ReactNode {
  const [videosState, setVideosState] = useState<LoadState<TrainingEvalVideoView[]>>({
    status: "idle",
  });

  useEffect(() => {
    let cancelled = false;
    setVideosState({ status: "loading" });
    listTrainingEvalRunRecordedVideos(evalRunId).then(
      (data) => {
        if (!cancelled) {
          setVideosState({ status: "success", data });
        }
      },
      (error: unknown) => {
        if (!cancelled) {
          setVideosState({
            status: "error",
            message:
              isHttpClientError(error) &&
              error.status === 404 &&
              error.code === "upstream.not_found"
                ? "영상 표시에 필요한 metadata 또는 Evaluation 정보를 찾을 수 없습니다."
                : resolveLoadErrorMessage(error),
          });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [evalRunId]);

  if (videosState.status === "idle" || videosState.status === "loading") {
    return (
      <LoadingState size="sm" mode="plain">
        Evaluation 영상을 불러오고 있습니다…
      </LoadingState>
    );
  }
  if (videosState.status === "error") {
    return (
      <EmptyState size="sm" mode="plain" tone="error">
        Evaluation 영상을 불러오지 못했습니다: {videosState.message}
      </EmptyState>
    );
  }
  return <GalleryBody videos={videosState.data} expectedFailures={expectedFailures} />;
}

function GalleryBody({
  videos,
  expectedFailures,
}: {
  videos: readonly TrainingEvalVideoView[];
  expectedFailures: number | null;
}): ReactNode {
  const gallery = useRecordedEpisodeGallery(videos);

  const missingFailureVideos = hasMissingFailureVideos(expectedFailures, gallery.counts.failures);

  return (
    <div className={styles.gallery}>
      {missingFailureVideos ? (
        <p className={styles.alert} role="alert">
          실패 에피소드가 {expectedFailures}건 있지만 실패 영상이 없습니다. 실패 영상은 필수
          산출물입니다. Evaluation 녹화 설정을 확인해 주세요.
        </p>
      ) : null}
      {gallery.episodes.length === 0 ? (
        <EmptyState size="sm" mode="plain">
          이 Evaluation에는 녹화된 영상이 없습니다.
        </EmptyState>
      ) : (
        <>
          <div className={styles.galleryToolbar}>
            <div className={styles.pager}>
              <IconButton
                text="이전"
                aria-label="이전 에피소드"
                size="sm"
                variant="surface"
                disabled={gallery.page === 0}
                onClick={gallery.goToPreviousEpisode}
              />
              <span className={styles.pageLabel}>
                {Math.min(gallery.page + 1, gallery.episodeCount)} / {gallery.episodeCount}
              </span>
              <IconButton
                text="다음"
                aria-label="다음 에피소드"
                size="sm"
                variant="surface"
                disabled={gallery.page >= gallery.episodeCount - 1}
                onClick={gallery.goToNextEpisode}
              />
            </div>
            <ContentSwitcher
              label="영상 Filter"
              size="sm"
              value={gallery.activeFilter}
              onValueChange={(value) => gallery.selectFilter(value as RecordedEpisodeFilter)}
              options={(Object.keys(filterLabels) as RecordedEpisodeFilter[]).map((value) => ({
                value,
                label: `${filterLabels[value]} (${gallery.counts[value]})`,
              }))}
            />
          </div>
          {gallery.episode !== null ? (
            <>
              <EpisodeMeta episode={gallery.episode} />
              <EpisodePlayer key={gallery.episode.key} episode={gallery.episode} />
            </>
          ) : (
            <EmptyState size="sm" mode="plain">
              선택한 필터에 해당하는 영상이 없습니다.
            </EmptyState>
          )}
        </>
      )}
    </div>
  );
}

function EpisodeMeta({ episode }: { episode: RecordedEpisode }): ReactNode {
  const failure = isFailureEpisode(episode);
  const success = isSuccessEpisode(episode);
  const outcome = failure
    ? { label: "실패", color: 0xdc2626 }
    : success
      ? { label: "성공", color: 0x16a34a }
      : { label: "미상", color: 0x64748b };
  return (
    <div className={styles.episodeMeta}>
      <span className={styles.episodeTitle}>{episodeTitle(episode)}</span>
      <LabelChip color={outcome.color} size="xs" variant="filled">
        {outcome.label}
      </LabelChip>
      {episode.outcome !== null ? <span className={styles.cardMeta}>{episode.outcome}</span> : null}
      {episode.failureReasons.map((reason) => (
        <span key={reason} className={styles.failureReason}>
          {reason}
        </span>
      ))}
    </div>
  );
}

function EpisodePlayer({ episode }: { episode: RecordedEpisode }): ReactNode {
  const channels = useMemo<VideoChannelSource[]>(
    () =>
      playableCameras(episode).map((camera) => ({
        id: camera.id,
        src: camera.url,
        label: camera.label,
        subLabel: camera.path,
      })),
    [episode],
  );
  return (
    <MultiChannelVideoPlayer
      channels={channels}
      emptyMessage="재생할 수 있는 카메라 영상이 없습니다."
      // 프레임 높이를 고정한다. 전폭으로 커진 영상은 지표와 영상을 번갈아 보는 작업을 방해한다.
      maxHeight="20rem"
      // 높이를 제한한 대신 하나를 자세히 볼 방법을 준다. 훑어보기는 그대로 두고 확대만 추가한다.
      zoomable
    />
  );
}
